import { useState, useRef } from "react";
import { Ban } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

// Filter category definitions
export const FILTER_CATEGORIES = [
  { id: "portrait", name: "Portrait" },
  { id: "landscape", name: "Landscape" },
  { id: "retro", name: "Retro" },
  { id: "film", name: "Film" },
  { id: "food", name: "Food" },
  { id: "vibe", name: "Vibe" },
  { id: "mood", name: "Mood" },
  { id: "b&w", name: "B&W" },
] as const;

export type FilterCategory = typeof FILTER_CATEGORIES[number]["id"];

// Individual filter definitions with CSS filter values
export interface Filter {
  id: string;
  name: string;
  category: FilterCategory;
  cssFilter: string;
  thumbnail?: string;
}

export const FILTERS: Filter[] = [
  // Portrait filters
  { id: "soft-glow", name: "Soft Glow", category: "portrait", cssFilter: "brightness(1.1) contrast(0.95) saturate(0.9)" },
  { id: "porcelain", name: "Porcelain", category: "portrait", cssFilter: "brightness(1.15) contrast(0.9) saturate(0.85)" },
  { id: "warm-skin", name: "Warm Skin", category: "portrait", cssFilter: "brightness(1.05) sepia(0.15) saturate(1.1)" },
  { id: "cool-tone", name: "Cool Tone", category: "portrait", cssFilter: "brightness(1.05) hue-rotate(-10deg) saturate(0.95)" },
  { id: "natural", name: "Natural", category: "portrait", cssFilter: "brightness(1.02) contrast(1.05) saturate(1.05)" },

  // Landscape filters
  { id: "vivid", name: "Vivid", category: "landscape", cssFilter: "brightness(1.1) contrast(1.15) saturate(1.3)" },
  { id: "dramatic", name: "Dramatic", category: "landscape", cssFilter: "brightness(0.95) contrast(1.25) saturate(1.1)" },
  { id: "golden-hour", name: "Golden Hour", category: "landscape", cssFilter: "brightness(1.1) sepia(0.25) saturate(1.2) contrast(1.05)" },
  { id: "misty", name: "Misty", category: "landscape", cssFilter: "brightness(1.15) contrast(0.85) saturate(0.8)" },
  { id: "deep-blue", name: "Deep Blue", category: "landscape", cssFilter: "brightness(1.05) hue-rotate(15deg) saturate(1.2)" },

  // Retro filters
  { id: "beige", name: "Beige", category: "retro", cssFilter: "sepia(0.35) brightness(1.05) contrast(0.95)" },
  { id: "cold-blue", name: "Cold Blue", category: "retro", cssFilter: "brightness(1.05) hue-rotate(180deg) saturate(0.7) sepia(0.1)" },
  { id: "pumpkin", name: "Pumpkin", category: "retro", cssFilter: "sepia(0.4) saturate(1.3) hue-rotate(-10deg) brightness(1.05)" },
  { id: "mocha", name: "Mocha", category: "retro", cssFilter: "sepia(0.5) brightness(0.95) contrast(1.1) saturate(0.8)" },
  { id: "green-orange", name: "Green Orange", category: "retro", cssFilter: "hue-rotate(30deg) saturate(1.2) contrast(1.05)" },
  { id: "orange-teal", name: "Orange Teal", category: "retro", cssFilter: "hue-rotate(-15deg) saturate(1.3) contrast(1.1)" },

  // Film filters
  { id: "kodak", name: "Kodak", category: "film", cssFilter: "sepia(0.2) saturate(1.4) contrast(1.1) brightness(1.05)" },
  { id: "fuji", name: "Fuji", category: "film", cssFilter: "saturate(1.2) hue-rotate(5deg) contrast(1.05)" },
  { id: "polaroid", name: "Polaroid", category: "film", cssFilter: "sepia(0.15) contrast(1.15) brightness(1.1) saturate(1.1)" },
  { id: "cinematic", name: "Cinematic", category: "film", cssFilter: "contrast(1.2) brightness(0.95) saturate(0.9)" },
  { id: "vintage", name: "Vintage", category: "film", cssFilter: "sepia(0.3) contrast(1.1) brightness(1.05)" },

  // Food filters
  { id: "fresh", name: "Fresh", category: "food", cssFilter: "brightness(1.15) saturate(1.3) contrast(1.05)" },
  { id: "warm", name: "Warm", category: "food", cssFilter: "brightness(1.1) sepia(0.1) saturate(1.2) contrast(1.05)" },
  { id: "crispy", name: "Crispy", category: "food", cssFilter: "brightness(1.1) contrast(1.2) saturate(1.15)" },
  { id: "delicious", name: "Delicious", category: "food", cssFilter: "brightness(1.08) saturate(1.25) hue-rotate(-5deg)" },
  { id: "appetizing", name: "Appetizing", category: "food", cssFilter: "brightness(1.12) contrast(1.1) saturate(1.35)" },

  // Vibe filters
  { id: "dreamy", name: "Dreamy", category: "vibe", cssFilter: "brightness(1.15) contrast(0.9) saturate(0.85)" },
  { id: "neon", name: "Neon", category: "vibe", cssFilter: "brightness(1.1) saturate(1.5) contrast(1.15)" },
  { id: "chill", name: "Chill", category: "vibe", cssFilter: "brightness(1.05) hue-rotate(10deg) saturate(0.9)" },
  { id: "energetic", name: "Energetic", category: "vibe", cssFilter: "brightness(1.1) contrast(1.2) saturate(1.4)" },
  { id: "aesthetic", name: "Aesthetic", category: "vibe", cssFilter: "brightness(1.08) sepia(0.1) saturate(1.15) hue-rotate(-5deg)" },

  // Mood filters
  { id: "melancholy", name: "Melancholy", category: "mood", cssFilter: "brightness(0.95) saturate(0.7) contrast(1.1)" },
  { id: "happy", name: "Happy", category: "mood", cssFilter: "brightness(1.15) saturate(1.2) contrast(1.05)" },
  { id: "mysterious", name: "Mysterious", category: "mood", cssFilter: "brightness(0.9) contrast(1.2) saturate(0.8)" },
  { id: "romantic", name: "Romantic", category: "mood", cssFilter: "brightness(1.08) sepia(0.15) saturate(1.1) hue-rotate(-5deg)" },
  { id: "peaceful", name: "Peaceful", category: "mood", cssFilter: "brightness(1.1) contrast(0.95) saturate(0.9)" },

  // B&W filters
  { id: "classic-bw", name: "Classic", category: "b&w", cssFilter: "grayscale(1) contrast(1.1)" },
  { id: "high-contrast-bw", name: "High Contrast", category: "b&w", cssFilter: "grayscale(1) contrast(1.4) brightness(1.05)" },
  { id: "soft-bw", name: "Soft", category: "b&w", cssFilter: "grayscale(1) contrast(0.9) brightness(1.1)" },
  { id: "noir", name: "Noir", category: "b&w", cssFilter: "grayscale(1) contrast(1.3) brightness(0.9)" },
  { id: "silver", name: "Silver", category: "b&w", cssFilter: "grayscale(0.9) contrast(1.15) brightness(1.05)" },
];

export interface FilterState {
  filterId: string | null;
  intensity: number;
}

interface FiltersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFilter: FilterState;
  onFilterChange: (filter: FilterState) => void;
  videoThumbnail?: string;
}

export default function FiltersDrawer({
  isOpen,
  onClose,
  selectedFilter,
  onFilterChange,
  videoThumbnail,
}: FiltersDrawerProps) {
  const [activeCategory, setActiveCategory] = useState<FilterCategory>("retro");
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const filterScrollRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  const filteredFilters = FILTERS.filter((f) => f.category === activeCategory);
  const currentFilter = selectedFilter.filterId
    ? FILTERS.find((f) => f.id === selectedFilter.filterId)
    : null;

  const handleFilterSelect = (filterId: string | null) => {
    onFilterChange({
      filterId,
      intensity: filterId ? selectedFilter.intensity : 100,
    });
  };

  const handleIntensityChange = (value: number[]) => {
    onFilterChange({
      ...selectedFilter,
      intensity: value[0],
    });
  };

  // Calculate the CSS filter with intensity applied
  const getFilterWithIntensity = (filter: Filter, intensity: number): string => {
    if (intensity === 100) return filter.cssFilter;

    // Parse the filter and interpolate towards no effect (identity)
    const factor = intensity / 100;
    const parts = filter.cssFilter.split(" ");

    return parts.map((part) => {
      const match = part.match(/^(\w+(?:-\w+)?)\(([^)]+)\)$/);
      if (!match) return part;

      const [, fn, valueStr] = match;

      // Identity values for each filter function
      const identityValues: Record<string, number> = {
        brightness: 1,
        contrast: 1,
        saturate: 1,
        sepia: 0,
        grayscale: 0,
        "hue-rotate": 0,
        blur: 0,
      };

      const identity = identityValues[fn] ?? 1;
      const value = parseFloat(valueStr);
      const unit = valueStr.replace(/[\d.-]/g, "");

      // Interpolate between identity and target value
      const interpolated = identity + (value - identity) * factor;

      return `${fn}(${interpolated}${unit})`;
    }).join(" ");
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-60 bg-black/95 rounded-t-2xl"
      style={{ maxHeight: "45vh" }}
    >
      {/* Drag handle */}
      <div className="flex justify-center py-3">
        <div className="w-10 h-1 bg-white/30 rounded-full" />
      </div>

      {/* Intensity slider */}
      {selectedFilter.filterId && (
        <div className="px-4 pb-4 flex items-center gap-4">
          <Slider
            value={[selectedFilter.intensity]}
            onValueChange={handleIntensityChange}
            max={100}
            min={0}
            step={1}
            className="flex-1 **:data-radix-slider-track:bg-white/20 **:data-radix-slider-range:bg-[#FF6B5B] **:data-radix-slider-thumb:bg-white **:data-radix-slider-thumb:border-0"
          />
          <span className="text-white text-sm w-8 text-right">{selectedFilter.intensity}</span>
        </div>
      )}

      {/* Category tabs */}
      <div
        ref={categoryScrollRef}
        className="flex items-center gap-1 px-4 pb-3 overflow-x-auto scrollbar-hide"
      >
        {/* No filter button */}
        <button
          onClick={() => handleFilterSelect(null)}
          className={cn(
            "shrink-0 p-2 rounded-lg transition-colors",
            !selectedFilter.filterId ? "text-white" : "text-white/50"
          )}
        >
          <Ban size={24} />
        </button>

        {/* Category tabs */}
        {FILTER_CATEGORIES.map((category) => (
          <button
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={cn(
              "shrink-0 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap",
              activeCategory === category.id
                ? "text-white border-b-2 border-white"
                : "text-white/50"
            )}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* Filter thumbnails */}
      <div
        ref={filterScrollRef}
        className="flex gap-3 px-4 pb-6 overflow-x-auto scrollbar-hide"
      >
        {filteredFilters.map((filter) => {
          const isSelected = selectedFilter.filterId === filter.id;
          const filterStyle = getFilterWithIntensity(
            filter,
            isSelected ? selectedFilter.intensity : 100
          );

          return (
            <button
              key={filter.id}
              onClick={() => handleFilterSelect(filter.id)}
              className="shrink-0 flex flex-col items-center gap-2"
            >
              {/* Thumbnail with filter applied */}
              <div
                className={cn(
                  "w-16 h-16 rounded-full overflow-hidden border-2 transition-all",
                  isSelected
                    ? "border-white ring-2 ring-white/50"
                    : "border-transparent"
                )}
              >
                {videoThumbnail ? (
                  <img
                    src={videoThumbnail}
                    alt={filter.name}
                    className="w-full h-full object-cover"
                    style={{ filter: filter.cssFilter }}
                  />
                ) : (
                  <div
                    className="w-full h-full bg-linear-to-br from-purple-500 via-pink-500 to-orange-500"
                    style={{ filter: filter.cssFilter }}
                  />
                )}
              </div>
              {/* Filter name */}
              <span
                className={cn(
                  "text-xs transition-colors",
                  isSelected ? "text-white" : "text-white/70"
                )}
              >
                {filter.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Helper function to apply filter to video element
export function getVideoFilterStyle(filterState: FilterState): string {
  if (!filterState.filterId) return "none";

  const filter = FILTERS.find((f) => f.id === filterState.filterId);
  if (!filter) return "none";

  if (filterState.intensity === 100) return filter.cssFilter;

  // Interpolate filter intensity
  const factor = filterState.intensity / 100;
  const parts = filter.cssFilter.split(" ");

  return parts.map((part) => {
    const match = part.match(/^(\w+(?:-\w+)?)\(([^)]+)\)$/);
    if (!match) return part;

    const [, fn, valueStr] = match;

    const identityValues: Record<string, number> = {
      brightness: 1,
      contrast: 1,
      saturate: 1,
      sepia: 0,
      grayscale: 0,
      "hue-rotate": 0,
      blur: 0,
    };

    const identity = identityValues[fn] ?? 1;
    const value = parseFloat(valueStr);
    const unit = valueStr.replace(/[\d.-]/g, "");

    const interpolated = identity + (value - identity) * factor;

    return `${fn}(${interpolated}${unit})`;
  }).join(" ");
}
